import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, ExternalLink, Download, Truck } from "lucide-react";
import api from "../lib/api";
import { ProductGallery } from "../components/ProductGallery";
import { LookInside } from "../components/LookInside";
import { ShareButtons } from "../components/ShareButtons";
import { ProductReviews } from "../components/ProductReviews";
import { ShippingFields } from "../components/ShippingFields";
import { GiftFields, EMPTY_GIFT, giftPayload } from "../components/GiftFields";
import { PromoField, PriceWithPromo } from "../components/PromoField";
import { useSeo } from "../lib/seo";
import { fileUrl } from "../lib/api";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [email, setEmail] = useState("");
  const [gift, setGift] = useState(EMPTY_GIFT);
  const [promo, setPromo] = useState(null);
  const [format, setFormat] = useState("");
  const [qty, setQty] = useState(1);
  const [country, setCountry] = useState("");
  const [quote, setQuote] = useState(null);
  const physical = p?.kind === "physical";
  const soldOut = physical && p?.stock !== null && p?.stock !== undefined && p.stock <= 0;
  useSeo({
    title: p ? `${p.name} — ${physical ? "Shipped Worldwide" : ({ ebook: "Christian Ebook", journal: "Prayer Journal", sticker: "Scripture Stickers", study: "Bible Study" })[p.category] || "Digital Download"}` : "Product",
    description: p ? `${p.description || p.name} ${physical ? "Ships worldwide" : `Instant digital download${p.files?.length > 1 ? ` in ${p.files.map((f) => f.label).join(", ")}` : ""}`}. $${(p.price_cents / 100).toFixed(2)} from Faithful Store.` : null,
    path: `/product/${id}`,
    image: p?.cover_key ? fileUrl(p.cover_key) : null,
    jsonLd: p ? { "@context": "https://schema.org", "@type": "Product", name: p.name, description: p.description, image: p.cover_key ? fileUrl(p.cover_key) : undefined, category: p.category, brand: { "@type": "Brand", name: "Faithful Store" },
      offers: { "@type": "Offer", price: (p.price_cents / 100).toFixed(2), priceCurrency: (p.currency || "usd").toUpperCase(), availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock", url: window.location.href } } : null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => toast.error("Not found"));
  }, [id]);

  const buy = async () => {
    if (!email) return toast.error(physical ? "Please enter your email for order updates." : "Please enter your email to receive your download.");
    if (physical && !country) return toast.error("Please choose the country we're shipping to.");
    if (gift.enabled && !gift.to) return toast.error("Please enter the recipient's email.");
    setLoading(true);
    try {
      const r = await api.post("/checkout", { product_id: id, email, promo_code: promo?.code, format: format || undefined, ...(physical ? { quantity: qty, ship_country: country } : giftPayload(gift)) });
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (!p) return <div className="max-w-7xl mx-auto px-6 py-24 text-[#4a4a4a]">Loading…</div>;
  const itemsCents = (promo ? promo.final_cents : p.price_cents) * (physical ? qty : 1);
  const buyLabel = physical ? `Buy · $${((itemsCents + (quote?.shipping_cents || 0)) / 100).toFixed(2)}${quote ? " + tax" : ""}` : `${gift.enabled ? "Gift" : "Buy"} · $${(itemsCents / 100).toFixed(2)}`;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <ProductGallery product={p} />
          {!physical && <LookInside product={p} />}
          <div className="mt-5"><ShareButtons url={window.location.href} title={p.name} description={p.description} image={p.cover_key ? fileUrl(p.cover_key) : null} testPrefix="product-share" /></div>
        </div>
        <div>
          <div className="overline text-[#d4af37]">{p.category}{physical && <span className="ml-3 text-[#0a192f] bg-[#d4af37]/20 px-2 py-0.5" data-testid="physical-badge">Ships to you</span>}</div>
          <h1 data-testid="product-title" className="font-serif text-5xl mt-4 text-[#0a192f] leading-tight tracking-tight">{p.name}</h1>
          <p className="text-lg text-[#4a4a4a] mt-6 leading-relaxed">{p.description}</p>
          {p.long_description && (
            <div className="mt-6 text-[#1a1a1a] leading-relaxed whitespace-pre-line">{p.long_description}</div>
          )}
          {p.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {p.tags.map((t) => (
                <span key={t} className="text-xs uppercase tracking-widest bg-[#0a192f] text-white px-3 py-1 font-semibold">{t}</span>
              ))}
            </div>
          )}
          <div className="divider-gold my-10" />
          <div className="text-4xl font-serif text-[#0a192f]"><PriceWithPromo cents={p.price_cents} promo={promo} testId="product-price" /></div>

          <div className="mt-8">
            <label className="overline text-[#4a4a4a]">{physical ? "Your email (for order & shipping updates)" : "Your email (for download delivery)"}</label>
            <input
              data-testid="buy-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="mt-2 w-full border border-[#e5e5e5] bg-white px-4 py-3 outline-none focus:border-[#0a192f]"
            />
          </div>
          <PromoField productId={id} promo={promo} setPromo={setPromo} />
          {physical && !soldOut && <ShippingFields product={p} qty={qty} setQty={setQty} country={country} setCountry={setCountry} onQuote={setQuote} />}
          {physical && p.stock !== null && p.stock !== undefined && p.stock > 0 && p.stock <= 5 && <p className="mt-3 text-sm font-semibold text-[#b45309]" data-testid="low-stock">Only {p.stock} left in stock</p>}
          {!physical && p.files?.length > 1 && (
            <div className="mt-6" data-testid="format-picker">
              <label className="overline text-[#4a4a4a]">Choose your format</label>
              <select data-testid="format-select" value={format} onChange={(e) => setFormat(e.target.value)} className="mt-2 w-full border border-[#e5e5e5] bg-white px-4 py-3 outline-none focus:border-[#0a192f]">
                {p.files.map((f) => <option key={f.key} value={f.label}>{f.label}{f.file_name ? ` — ${f.file_name}` : ""}</option>)}
              </select>
              <p className="text-xs text-[#4a4a4a] mt-2">Your pick becomes the default download; every format stays available in My Library.</p>
            </div>
          )}
          {!physical && <GiftFields gift={gift} setGift={setGift} />}

          <div className="mt-6 flex flex-wrap gap-3">
            {soldOut ? (
              <span data-testid="sold-out" className="inline-flex items-center px-6 py-3 bg-[#f4f5f7] text-[#4a4a4a] font-semibold uppercase tracking-widest text-sm">Sold out</span>
            ) : (
              <button data-testid="buy-btn" onClick={buy} disabled={loading} className="btn-navy inline-flex items-center gap-2 disabled:opacity-50">
                <ShoppingBag className="w-4 h-4" /> {loading ? "Redirecting…" : buyLabel}
              </button>
            )}
            {p.gumroad_url && (
              <a data-testid="gumroad-btn" href={p.gumroad_url} target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center gap-2">
                <ExternalLink className="w-4 h-4" /> Buy on Gumroad
              </a>
            )}
          </div>
          <p className="text-xs text-[#4a4a4a] mt-4 flex items-center gap-2">
            {physical ? <><Truck className="w-3.5 h-3.5" /> Ships worldwide · shipping by weight, sales tax and secure payment handled at checkout.</> : <><Download className="w-3.5 h-3.5" /> Instant digital delivery{p.files?.length > 1 ? ` · ${p.files.length} formats included (${p.files.map((f) => f.label).join(", ")})` : ""}. Secure download link valid 14 days.</>}
          </p>
        </div>
      </div>
      <ProductReviews productId={id} />
    </div>
  );
}
